// TrustPill — renders a small inline label for a dashboard metric's
// data-integrity classification. Source-of-truth contract is defined
// server-side at packages/server/src/lib/trust.ts.
//
// Used per the 2026-05-15 dashboard trust hardening rule: every card
// that surfaces a business metric must show how trustworthy that
// metric is at a glance. The UI MUST NOT label a metric "Verified"
// unless the API response says so via dataIntegrity.<field>.level.

import React from "react";

export type TrustLevel = "verified" | "directional" | "contaminated" | "blocked";

export interface TrustLabel {
  level: TrustLevel;
  reason?: string;
  caveat?: string;
}

interface TrustPillProps {
  label?: TrustLabel;
  /** Defaults to small; pass "md" for header-level cards. */
  size?: "sm" | "md";
}

const TONE: Record<TrustLevel, { bg: string; fg: string; ring: string; symbol: string; text: string }> = {
  verified:     { bg: "bg-emerald-50",  fg: "text-emerald-700",  ring: "ring-emerald-200",  symbol: "✓", text: "Verified" },
  directional:  { bg: "bg-amber-50",    fg: "text-amber-700",    ring: "ring-amber-200",    symbol: "~", text: "Directional" },
  contaminated: { bg: "bg-rose-50",     fg: "text-rose-700",     ring: "ring-rose-200",     symbol: "!", text: "Contaminated" },
  blocked:      { bg: "bg-slate-100",   fg: "text-slate-600",    ring: "ring-slate-300",    symbol: "▢", text: "Blocked" },
};

export function TrustPill({ label, size = "sm" }: TrustPillProps): React.ReactElement | null {
  if (!label) return null;
  const tone = TONE[label.level];
  if (!tone) return null;
  const padding = size === "md" ? "px-2 py-0.5 text-xs" : "px-1.5 py-px text-[10px]";
  const title = [tone.text, label.reason, label.caveat].filter(Boolean).join(" — ");
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 ${padding} rounded-full ring-1 ring-inset ${tone.ring} ${tone.bg} ${tone.fg} font-medium uppercase tracking-wide`}
    >
      <span aria-hidden="true">{tone.symbol}</span>
      <span>{tone.text}</span>
    </span>
  );
}
